import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Stack,
  SxProps,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import {
  useAssetTableContext,
  useAssetTableDispatch,
} from "@hooks/use-table-context/asset";
import { DataTable } from "@components/data-table";
import { dataTableResponsiveWidth } from "@components/data-table/utils";
import { FilterType } from "@components/data-table/types";
import { TableDesktopFooterMenu } from "@components/data-table/data-table-desktop-footer-menu";
import { DataTableDesktopHeaderMenu } from "@components/data-table/data-table-desktop-header-menu";
import { TableSearchInput } from "@components/data-table/search-input";
import { TableSettingsMenu } from "@components/data-table/settings-menu";
import { TableMobileMenu } from "@components/data-table/mobile-menu";
import { TableFilterInput } from "@components/data-table/filter-input";
import { LoadingTableBlock } from "@components/data-table/loading-table-block";
import { TableEditItemsPopover } from "@components/data-table/edit-items-popover";
import { TableDeleteItemsPopover } from "@components/data-table/delete-items-popover";
import { ScannerPopover } from "@components/data-table/scanner-popover";
import { WpAsset } from "@hooks/use-get/assets/types";
import { useGetAssets } from "@hooks/use-get/assets";
import { useTableAnchorsState } from "../../../hooks/use-table-anchors-state";
import { AssetTableColumnFromApi } from "../../../hooks/use-config-context/asset/types";
import { useAssetsConfigContext } from "../../../hooks/use-config-context/asset";
import {
  getOptionsFromSelectFieldConfig,
  isFieldFilterable,
} from "@hooks/use-config/utils";
import {
  ID_KEY as ASSET_ID_KEY,
  PER_PAGE_OPTIONS,
} from "../../../hooks/use-table-context/utils";
import { useCapabilities } from "../../../hooks/use-capabilities";
import { AssetTableFilter } from "@hooks/use-table-context/asset/types";
import { formatAssetForSave } from "@utils/format-asset-for-save";
import { buildAssetTableCellFormatters } from "../utils";
import { getStateValuesFromField } from "../helperFunctions";

const getAssetTableData = (
  items: WpAsset[],
  columns: AssetTableColumnFromApi[]
) => {
  return items.map((asset) => {
    const assetData = columns.reduce((acc, column) => {
      const value = column.getValue(asset as any);
      return { ...acc, [column.id]: value };
    }, {});
    return { ...assetData, id: asset.id };
  });
};

export const AssetTableComponent = ({
  sx,
  hideAddAsset = false,
  openAssetModal,
}: {
  sx?: SxProps;
  hideAddAsset?: boolean;
  openAssetModal?: (data?: any) => void;
}) => {
  const mobileAnchorRef = useRef<HTMLDivElement>(null);
  const tableStackRef = useRef<HTMLDivElement>(null);
  const {
    searchMenuAnchor,
    setSearchMenuAnchor,
    filterMenuAnchor,
    setFilterMenuAnchor,
    editAssetsAnchor,
    setEditAssetsAnchor,
    deleteAssetsAnchor,
    setDeleteAssetsAnchor,
    settingsAnchor,
    setSettingsAnchor,
  } = useTableAnchorsState();
  const { can } = useCapabilities();
  const canCreate = can("create_assets");
  const canEdit = can("edit_assets");
  const canDelete = can("delete_assets");
  const { getOneAssetByAssetTag } = useGetAssets();

  const [shouldShowDesktopFooter, setShouldShowDesktopFooter] =
    useState<boolean>(window.innerWidth >= 900);
  const [showScanner, setShowScanner] = useState<boolean>(false);

  const { tableColumns, config: assetsConfig } = useAssetsConfigContext();

  const [filtersToRemove, setFiltersToRemove] = useState<AssetTableFilter[]>(
    [],
  );

  const {
    tableData,
    loading,
    disabledRows,
    selectedItems,
    page,
    perPage,
    total,
    hiddenColumns,
    filters,
  } = useAssetTableContext();

  const {
    selectItem,
    editItems,
    deleteItems,
    addFilter,
    setPage,
    setPerPage,
    addHiddenColumn,
    removeHiddenColumn,
    removeAllFilters,
    removeFilter,
  } = useAssetTableDispatch();

  const setSearchMenuAnchorMobile = () =>
    mobileAnchorRef.current && setSearchMenuAnchor(mobileAnchorRef.current);
  const setFilterMenuAnchorMobile = () =>
    mobileAnchorRef.current && setFilterMenuAnchor(mobileAnchorRef.current);

  useEffect(() => {
    const handleResize = () => {
      const isDesktop = window.innerWidth >= 900;
      if (shouldShowDesktopFooter !== isDesktop)
        setShouldShowDesktopFooter(isDesktop);
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [shouldShowDesktopFooter]);

  const activeTableColumns = useMemo(
    () => tableColumns?.filter((column) => !hiddenColumns?.includes(column.id)),
    [tableColumns, hiddenColumns],
  );

  const assetTableLabels = useMemo(
    () =>
      tableColumns.reduce(
        (acc, column) => ({
          ...acc,
          [column.id]: column.label,
        }),
        {} as Record<string, string>,
      ),
    [tableColumns],
  );

  const assetTableIds = useMemo(
    () => activeTableColumns.map((column) => column.id),
    [activeTableColumns],
  );
  
  return (
    <Box sx={{ width: "100%", position: "relative", ...sx }}>
      <Stack className="asset-table-stack table-stack" ref={tableStackRef}>
        {/* {hasTableDataLoaded && (
          <> */}

        <DataTableDesktopHeaderMenu
          setSearchAnchor={setSearchMenuAnchor}
          setFilterAnchor={setFilterMenuAnchor}
          setSettingsAnchor={setSettingsAnchor}
          useTableContext={useAssetTableContext}
          openScanner={() => setShowScanner(true)}
        />
        <div className="controls-bar mobile top" />
        <div id="asset-table-mobile-anchor" ref={mobileAnchorRef} />
        {/* </>
        )} */}

        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          open={tableData.length > 0 && loading}
        >
          <Alert
            elevation={8}
            severity="info"
            sx={{
              width: "100%",
              "& .MuiAlert-message": {
                flexGrow: 1,
              },
            }}
          >
            <Stack
              direction="row"
              spacing={3}
              alignItems="center"
              justifyContent="space-between"
              sx={{ pr: 1, width: "100%" }}
            >
              <span>Loading Table Data</span>
              <CircularProgress size={20} />
            </Stack>
          </Alert>
        </Snackbar>

        {(!tableData.length || !activeTableColumns.length) && loading && (
          <LoadingTableBlock type="Asset" />
        )}

        {tableData.length > 0 && activeTableColumns.length > 0 && (
          <DataTable
            sx={{ width: dataTableResponsiveWidth, m: "0 auto" }}
            data={getAssetTableData(tableData, tableColumns)}
            getKey={(row) => row.id as string}
            labels={assetTableLabels}
            formatCell={{
              ...buildAssetTableCellFormatters(
                activeTableColumns,
                assetsConfig,
                openAssetModal,
                tableData,
              ),
            }}
            onEdit={
              canEdit
                ? () => setEditAssetsAnchor(tableStackRef.current)
                : undefined
            }
            onDelete={
              canDelete
                ? () => setDeleteAssetsAnchor(tableStackRef.current)
                : undefined
            }
            bottomWidget={
              canCreate &&
              !hideAddAsset &&
              shouldShowDesktopFooter && (
                <TableDesktopFooterMenu
                  openModal={openAssetModal}
                  tableContext={useAssetTableContext}
                  buttonLabel="Add New Asset"
                />
              )
            }
            disabledRows={disabledRows}
            onSelect={selectItem}
            onDeselect={selectItem}
            selectedRows={selectedItems}
            page={page}
            perPage={perPage}
            perPageOptions={PER_PAGE_OPTIONS}
            onPageChange={setPage}
            onPerPageChange={setPerPage}
            total={total}
            hiddenColumns={
              (hiddenColumns ?? []).length > 0 ? hiddenColumns : [ASSET_ID_KEY]
            }
          />
        )}

        {tableData.length === 0 && !loading && (
          <Box sx={{ p: 3, textAlign: "center" }}>
            No Assets found.
            {filters.length > 0 && (
              <Box sx={{ mt: 2 }}>
                Try adjusting filters to see more results.
              </Box>
            )}
            {canCreate && !hideAddAsset && shouldShowDesktopFooter && (
              <Box sx={{ mt: 3 }}>
                <Button
                  color="success"
                  onClick={openAssetModal}
                  startIcon={<AddIcon />}
                  variant="contained"
                  sx={{
                    borderRadius: 2,
                  }}
                >
                  Add New Asset
                </Button>
              </Box>
            )}
          </Box>
        )}

        <TableMobileMenu
          openScanner={() => setShowScanner(true)}
          setSearchAnchor={setSearchMenuAnchorMobile}
          setFilterAnchor={setFilterMenuAnchorMobile}
          openModal={canCreate ? openAssetModal : undefined}
          filters={filters}
          newLabel="New Asset"
        />
      </Stack>

      <TableSearchInput
        anchor={tableData.length ? searchMenuAnchor : null}
        onClose={() => setSearchMenuAnchor(null)}
        useTableDispatch={useAssetTableDispatch}
        placeholderLabel="Search Asset Tags"
      />

      <TableFilterInput
        keyOptions={
          assetTableIds.length
            ? assetTableIds.filter((key) =>
                isFieldFilterable(assetsConfig[key]),
              )
            : null
        }
        keyLabels={assetTableLabels}
        anchor={filterMenuAnchor}
        onClose={() => setFilterMenuAnchor(null)}
        filters={filters}
        tableColumns={tableColumns}
        tableConfig={assetsConfig}
        addFilter={addFilter}
        removeFilter={removeFilter}
        removeAllFilters={removeAllFilters}
        filtersToRemove={filtersToRemove}
        setFiltersToRemove={setFiltersToRemove}
        getOptionsFormSelectFieldConfig={getOptionsFromSelectFieldConfig}
      />

      <TableEditItemsPopover
        keyLabels={assetTableLabels}
        keyOptions={assetTableIds.length ? assetTableIds : null}
        anchor={editAssetsAnchor}
        onClose={() => setEditAssetsAnchor(null)}
        config={assetsConfig}
        tableColumns={activeTableColumns}
        editItems={editItems}
        formatForSave={formatAssetForSave}
        itemLabel="Assets"
        itemIds={selectedItems}
        resolveFieldStateValues={getStateValuesFromField}
      />

      <TableDeleteItemsPopover
        anchor={deleteAssetsAnchor}
        onClose={() => setDeleteAssetsAnchor(null)}
        deleteItems={deleteItems}
        selectedItems={selectedItems}
        itemLabel="Asset"
      />

      <TableSettingsMenu
        anchor={settingsAnchor}
        onClose={() => setSettingsAnchor(null)}
        keyLabels={assetTableLabels}
        keyOptions={tableColumns.map((column) => column.id)}
        hiddenColumns={hiddenColumns}
        addHiddenColumn={addHiddenColumn}
        removeHiddenColumn={removeHiddenColumn}
        removeOptions={[ASSET_ID_KEY]}
      />

      <ScannerPopover
        isOpen={showScanner}
        onClose={() => setShowScanner(false)}
        onScan={async (scannedCode: string) => {
          // Match the scanned code against the real Asset Tag (title text), falling back to Serial Number.
          // getAssets translates a "title" filter into the native search endpoint under the hood.
          const matchedAsset = await getOneAssetByAssetTag(scannedCode);
          addFilter({
            column: (matchedAsset ? "title" : "serial_number") as keyof WpAsset,
            id: Date.now(),
            type: matchedAsset ? FilterType.Contains : FilterType.Is,
            value: matchedAsset ? matchedAsset.title.rendered : scannedCode,
          });
        }}
      />
    </Box>
  );
};

export default AssetTableComponent;
